<script setup lang="ts">
/**
 * 位于两面板邻接边中点的拖动手柄；用于调整左栏 / 右栏 / 底栏尺寸。
 */
defineProps<{
  orientation: 'vertical' | 'horizontal'
}>()

const emit = defineEmits<{
  start: [PointerEvent]
}>()

function onPointerDown(ev: PointerEvent): void {
  ev.preventDefault()
  emit('start', ev)
}
</script>

<template>
  <div
    class="split"
    :class="orientation === 'vertical' ? 'split--v' : 'split--h'"
    role="separator"
    :aria-orientation="orientation === 'vertical' ? 'vertical' : 'horizontal'"
    tabindex="-1"
    @pointerdown="onPointerDown"
  >
    <span class="split__track" aria-hidden="true" />
    <span class="split__ico" aria-hidden="true">
      <svg v-if="orientation === 'vertical'" viewBox="0 0 24 24" width="14" height="14">
        <circle cx="9" cy="8" r="1.35" fill="currentColor" />
        <circle cx="15" cy="8" r="1.35" fill="currentColor" />
        <circle cx="9" cy="12" r="1.35" fill="currentColor" />
        <circle cx="15" cy="12" r="1.35" fill="currentColor" />
        <circle cx="9" cy="16" r="1.35" fill="currentColor" />
        <circle cx="15" cy="16" r="1.35" fill="currentColor" />
      </svg>
      <svg v-else viewBox="0 0 24 24" width="14" height="14">
        <circle cx="8" cy="9" r="1.35" fill="currentColor" />
        <circle cx="12" cy="9" r="1.35" fill="currentColor" />
        <circle cx="16" cy="9" r="1.35" fill="currentColor" />
        <circle cx="8" cy="15" r="1.35" fill="currentColor" />
        <circle cx="12" cy="15" r="1.35" fill="currentColor" />
        <circle cx="16" cy="15" r="1.35" fill="currentColor" />
      </svg>
    </span>
  </div>
</template>

<style scoped>
.split {
  position: relative;
  flex-shrink: 0;
  touch-action: none;
  z-index: 2;
  user-select: none;
}

.split--v {
  width: 10px;
  cursor: ew-resize;
  margin: 0 -3px;
  align-self: stretch;
}

.split--h {
  height: 10px;
  margin: -3px 0;
  cursor: ns-resize;
  width: 100%;
}

.split__track {
  position: absolute;
  background: var(--im-border);
  pointer-events: none;
}

.split--v .split__track {
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  transform: translateX(-50%);
}

.split--h .split__track {
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  transform: translateY(-50%);
}

.split__ico {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--im-muted-2);
  opacity: 0.72;
  pointer-events: none;
  background: #fff;
  border-radius: 6px;
  border: 1px solid var(--im-border);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  padding: 2px;
  display: grid;
  place-items: center;
}

.split:hover .split__ico,
.split:focus-visible .split__ico {
  color: var(--im-text);
  opacity: 1;
  border-color: var(--im-border-strong);
}

.split:focus-visible {
  outline: 2px solid rgba(17, 17, 17, 0.2);
  outline-offset: 1px;
}

.split--v:hover .split__track,
.split--v:active .split__track {
  width: 2px;
  background: var(--im-border-strong);
}

.split--h:hover .split__track,
.split--h:active .split__track {
  height: 2px;
  background: var(--im-border-strong);
}
</style>
